/**
 * Insight casinos: join GetCasinoInsights rows to GetGeoJSON (floorPlanGeo)
 * slot features. Insight rows NEVER build an area+section+location key
 * (machines has no area, so that key starts with "null…" and matches nothing).
 *
 * Sole join (insight rows only):
 *   compact(row.floorLocation) == compact(feature.properties.location_string)
 * No init_slot_number / mapKey / mapFeatureId / area+section+location fallbacks.
 * Features without location_string get no machine.
 * Non-insight rows keep FloorMap's legacy raw-concat lookup.
 *
 * Compact key = same as Explore floor search: lowercase + strip non-alphanumeric
 * so "22A" / "22 A" / "22-A" match. Leading zeros are kept ('0801' ≠ '801').
 */

import { compactFloorLocationKey } from 'lib/insightSearchMatch';

type Props = { [k: string]: unknown } | null | undefined;
type Row = object | null | undefined;
const get = (r: Row, k: string): unknown => (r ? (r as Record<string, unknown>)[k] : undefined);
type Feature = { id?: number | string; properties?: Props };

/** Normalized string compare value: String + trim; null/undefined/'' -> ''. */
export const normStr = (v: unknown): string => (v === null || v === undefined ? '' : String(v).trim());

/** Re-export search compact so map-join callers share one definition. */
export { compactFloorLocationKey };

const s2 = (v: unknown): string => (v === null || v === undefined ? '' : String(v));

/** Case-insensitive property read (location_string / Location_String / …). */
const pickProp = (obj: Props, name: string): unknown => {
  if (!obj) return undefined;
  if (obj[name] !== undefined) return obj[name];
  const lower = name.toLowerCase();
  for (const k of Object.keys(obj)) if (k.toLowerCase() === lower) return obj[k];
  return undefined;
};

/**
 * Read GeoJSON feature location_string from properties (case-insensitive) or a
 * single nested `properties.properties` bag. Never init_slot_number / other keys.
 */
export const featureLocationString = (rawProps: Props): string => {
  let p = rawProps;
  if (!p) return '';
  const nested = p.properties;
  if (
    nested &&
    typeof nested === 'object' &&
    pickProp(p, 'location_string') === undefined &&
    pickProp(nested as Props, 'location_string') !== undefined
  ) {
    p = nested as Props;
  }
  const v = pickProp(p, 'location_string');
  return v === null || v === undefined ? '' : String(v).trim();
};

/**
 * Feature key in Bob's mapKey format (!2116): `${area}${section}${location}`
 * with null/undefined parts as '' (s2). Kept for tests / diagnostics; not used
 * for insight placement (location_string is the only join key).
 */
export const featureMapKey = (p: Props): string =>
  p ? `${s2(p.area)}${s2(p.section)}${s2(p.location)}` : '';
export const featureMapKeyRaw = (p: Props): string =>
  p ? `${p.area}${p.section}${p.location}` : '';

export interface FeatureIndex {
  /** compact(location_string) -> feature id. Ambiguous keys omitted. */
  byLocationString: Map<string, number | string>;
}

/** localStorage / window flag: set `insightMapJoinDebug=1` to log join stats. */
export const isInsightMapJoinDebug = (): boolean => {
  try {
    if (typeof window !== 'undefined' && (window as { __INSIGHT_MAP_JOIN_DEBUG__?: unknown }).__INSIGHT_MAP_JOIN_DEBUG__) {
      return true;
    }
    if (typeof localStorage !== 'undefined' && localStorage.getItem('insightMapJoinDebug') === '1') {
      return true;
    }
  } catch {
    // ignore
  }
  return false;
};

/**
 * Prefer current polygons when several share a location_string (historical
 * revisions). Higher score wins; equal scores with different ids → ambiguous.
 * Does not drop a lone feature that has a past revision_end + is_active=1.
 */
export const featureCurrencyScore = (p: Props): number => {
  let score = 0;
  const active = pickProp(p, 'is_active');
  if (active === 0 || active === false || active === '0') score -= 10;
  else score += 2;
  const re = pickProp(p, 'revision_end');
  if (re === null || re === undefined || String(re).trim() === '') {
    score += 2;
  } else {
    const t = Date.parse(String(re));
    if (Number.isFinite(t)) score += t >= Date.now() ? 1 : -1;
  }
  return score;
};

export const buildFeatureIndex = (features: Feature[] | undefined | null): FeatureIndex => {
  const idx: FeatureIndex = { byLocationString: new Map() };
  const dupLoc = new Set<string>();
  const scoreById = new Map<number | string, number>();
  type IndexedFeature = Feature & { id: number | string };
  const list: IndexedFeature[] = [];
  let skippedNoLoc = 0;

  (features || []).forEach((f, i) => {
    if (!f) return;
    // Prefer an existing id; otherwise synthesize so features without
    // kp_floor_plan_location_pk still join (LOTC floorPlanGeo often omits it).
    const fid =
      f.id !== undefined && f.id !== null
        ? f.id
        : `insight-feat-${i}`;
    list.push({ ...f, id: fid });
  });

  for (const f of list) {
    const p = f.properties;
    const fid = f.id;
    const locRaw = featureLocationString(p);
    if (!locRaw) {
      skippedNoLoc += 1;
      continue;
    }
    const loc = compactFloorLocationKey(locRaw);
    if (!loc || dupLoc.has(loc)) {
      if (dupLoc.has(loc)) continue;
      skippedNoLoc += 1;
      continue;
    }
    const score = featureCurrencyScore(p);
    scoreById.set(fid, score);
    const prev = idx.byLocationString.get(loc);
    if (prev === undefined) {
      idx.byLocationString.set(loc, fid);
      continue;
    }
    if (prev === fid) continue;
    const prevScore = scoreById.get(prev) ?? 0;
    if (score > prevScore) {
      idx.byLocationString.set(loc, fid);
    } else if (score < prevScore) {
      // keep prev
    } else {
      idx.byLocationString.delete(loc);
      dupLoc.add(loc);
    }
  }

  if (isInsightMapJoinDebug()) {
    // eslint-disable-next-line no-console
    console.info('[insightMapJoin] index', {
      features: (features || []).length,
      indexed: idx.byLocationString.size,
      skippedNoLoc,
      dupKeys: Array.from(dupLoc).slice(0, 20),
      sampleKeys: Array.from(idx.byLocationString.keys()).slice(0, 20),
    });
  }
  return idx;
};

/** Feature id for an insight row, or undefined. Sole key: location_string. */
export const resolveInsightFeatureId = (idx: FeatureIndex, r: Row): number | string | undefined => {
  if (!r) return undefined;
  const fl = get(r, 'floorLocation') as string | number | null | undefined;
  const flKey = compactFloorLocationKey(fl);
  if (!flKey) return undefined;
  return idx.byLocationString.get(flKey);
};

// ---------- feature -> row placement (built once per slotData) ----------

export interface RowPlacement<T> {
  /** Feature id -> the single row on it (features with 2+ rows are left out). */
  rowByFeature: Map<number | string, T>;
  /** Same map keyed by normStr(feature id), built once (click lookup). */
  rowByFeatureKey: Map<string, T>;
  /** Features that 2+ rows resolve to: ambiguous, left unmatched. */
  ambiguous: Set<number | string>;
}

/** One pass over the rows; a feature shared by two rows is ambiguous. */
export const buildRowPlacement = <T extends object>(idx: FeatureIndex, rows: T[] | undefined | null): RowPlacement<T> => {
  const rowByFeature = new Map<number | string, T>();
  const ambiguous = new Set<number | string>();
  let resolved = 0;
  let unresolved = 0;
  for (const r of rows || []) {
    if (get(r, 'isInsightRow') !== true) continue;
    const id = resolveInsightFeatureId(idx, r);
    if (id === undefined || ambiguous.has(id)) {
      unresolved += 1;
      continue;
    }
    if (rowByFeature.has(id)) {
      rowByFeature.delete(id);
      ambiguous.add(id);
      unresolved += 1;
    } else {
      rowByFeature.set(id, r);
      resolved += 1;
    }
  }
  const rowByFeatureKey = new Map<string, T>();
  rowByFeature.forEach((row, id) => rowByFeatureKey.set(normStr(id), row));
  if (isInsightMapJoinDebug()) {
    // eslint-disable-next-line no-console
    console.info('[insightMapJoin] place', {
      rows: (rows || []).length,
      resolved,
      unresolved,
      ambiguous: ambiguous.size,
      sampleUnresolved: (rows || [])
        .filter((r) => get(r, 'isInsightRow') === true && resolveInsightFeatureId(idx, r) === undefined)
        .slice(0, 10)
        .map((r) => ({ mnum: get(r, 'mnum'), floorLocation: get(r, 'floorLocation'), onMap: get(r, 'onMap') })),
    });
  }
  return { rowByFeature, rowByFeatureKey, ambiguous };
};

// Module-level state for the active floor plan and rows (FloorMap keeps its
// legacy lookup the same way). `insightIndexVersion` bumps on every rebuild so
// FloorMap effects can depend on it.
let activeIndex: FeatureIndex = buildFeatureIndex([]);
let activePlacement: RowPlacement<object> = { rowByFeature: new Map(), rowByFeatureKey: new Map(), ambiguous: new Set() };
let version = 0;
export const insightIndexVersion = (): number => version;

export const setInsightFeatureIndex = (features: Feature[] | undefined | null): number => {
  activeIndex = buildFeatureIndex(features);
  activePlacement = { rowByFeature: new Map(), rowByFeatureKey: new Map(), ambiguous: new Set() };
  version += 1;
  return version;
};

/** Build the feature -> row map for the current rows (call once per slotData). */
export const setInsightRows = (rows: object[] | undefined | null): void => {
  activePlacement = buildRowPlacement(activeIndex, rows);
};

/** Feature id for an insight row; undefined when unresolved or the feature is shared. */
export const insightFeatureId = (r: Row): number | string | undefined => {
  const id = resolveInsightFeatureId(activeIndex, r);
  return id !== undefined && activePlacement.ambiguous.has(id) ? undefined : id;
};

/** The single insight row on a feature (click), or undefined. */
export const insightRowForFeature = (featureId: unknown): object | undefined => {
  if (featureId === undefined || featureId === null) return undefined;
  return activePlacement.rowByFeatureKey.get(normStr(featureId));
};
